import type { BidResultFact } from "@/lib/bondStateModel";
import type {
  CapacityProject,
  CapacityProjectPayApplication,
  CapacityProjectState,
  CapacityRequestView,
} from "@/lib/capacityRequest";
import { formatBondCurrency } from "@/lib/bondValueDisplay";

// BRK-S402 — the segments of the aggregate-utilized bar on the Surety program
// panel (`ProgramUtilization`).
//
// Everything here is READ from what PMS serves, never derived from a rule of
// ours: one segment per order holding capacity (`GET …/capacity-projects`), its
// kind from the served `basis`, and the pending capacity request's credit from
// `capacityRequest.bondSubmissionId` + `amount` (PMS-S844). The
// used and available figures the broker reads are the account read's own
// (`preapproval.capacity`); the only arithmetic here is summing served amounts
// to lay the bar out.

/** What a segment is, for its colour, legend and action. */
export type SegmentKind = "bid" | "pp" | "other";

/**
 * The kind, from the basis PMS measured the hold in. An ESTIMATED price is a
 * bid not yet won; a FINAL price — or one an approved WIP report has since
 * adjusted — is a performance and payment job. A basis this build has not
 * adopted reads as a neutral "Bond" with no action, never a guess.
 */
export function segmentKind(basis: unknown): SegmentKind {
  if (basis === "estimated_contract_price") return "bid";
  if (basis === "final_contract_price" || basis === "wip_report") return "pp";
  return "other";
}

const KIND_LABELS: Record<SegmentKind, string> = {
  bid: "Bid bond",
  pp: "Performance & payment bond",
  other: "Bond",
};

export function segmentKindLabel(kind: SegmentKind): string {
  return KIND_LABELS[kind];
}

export interface AggregateSegment {
  orderId: string;
  orderNumber: string;
  projectName: string | null;
  /** Whole USD as served (D-10): formatted only for display. */
  amount: number;
  kind: SegmentKind;
  state: CapacityProjectState;
  /** The pending request's credit when it names this order; else null. */
  creditInReview: number | null;
  /** The order's latest pay application, as served (BRK-S451). */
  payApplication: CapacityProjectPayApplication | null;
  /** Where the order's bid stands, as served (BRK-S442). */
  bidResult: BidResultFact | null;
}

/** A pending credit for an order holding no segment, drawn on its own. */
export interface LooseCredit {
  orderId: string;
  amount: number;
}

export interface AggregateLayout {
  segments: AggregateSegment[];
  looseCredit: LooseCredit | null;
}

/** The credit a request puts in review: only while it is pending, and only
 *  when it names the order and a positive amount. */
function pendingCredit(
  request: CapacityRequestView | null | undefined,
): LooseCredit | null {
  if (!request || request.status !== "pending") return null;
  const { bondSubmissionId, amount } = request;
  if (!bondSubmissionId || typeof amount !== "number" || !(amount > 0)) {
    return null;
  }
  return { orderId: bondSubmissionId, amount };
}

export function aggregateSegments(
  projects: readonly CapacityProject[],
  request: CapacityRequestView | null | undefined,
): AggregateLayout {
  const credit = pendingCredit(request);
  const segments = projects.map(
    (p): AggregateSegment => ({
      orderId: p.orderId,
      orderNumber: p.orderNumber,
      projectName: p.projectName,
      amount: p.amount,
      kind: segmentKind(p.basis),
      state: p.state,
      creditInReview:
        credit && credit.orderId === p.orderId ? credit.amount : null,
      payApplication: p.payApplication,
      bidResult: p.bidResult,
    }),
  );
  const placed = credit && segments.some((s) => s.orderId === credit.orderId);
  return { segments, looseCredit: placed ? null : credit };
}

/**
 * What the bar's widths are measured against: the aggregate limit, or the drawn
 * total when the served holds run past it, so the bar never overflows its
 * track. Layout only — the broker reads PMS's figures, not this.
 */
export function layoutScale(
  limit: number | null,
  segments: readonly { amount: number }[],
  looseCredit: LooseCredit | null,
): number {
  const drawn =
    segments.reduce((sum, s) => sum + Math.max(0, s.amount), 0) +
    (looseCredit?.amount ?? 0);
  return Math.max(limit ?? 0, drawn) || 1;
}

/** A width on the track, as a percentage clamped to 0–100. */
export function segmentWidthPercent(amount: number, scale: number): number {
  if (!(scale > 0)) return 100;
  const pct = (amount / scale) * 100;
  return Math.min(100, Math.max(0, Math.round(pct * 100) / 100));
}

/** The action a segment offers, or null for none. A P&P offers progress only
 *  once its hold is locked (the bond in force): a pay application is filed for
 *  nothing else, so an open P&P, still being issued, offers none (BRK-S404). */
export function segmentActionLabel(
  segment: Pick<AggregateSegment, "kind" | "state">,
): string | null {
  if (segment.kind === "bid") return "Report bid result";
  if (segment.kind === "pp" && segment.state === "locked") {
    return "Update progress";
  }
  return null;
}

function creditPhrase(amount: number): string {
  return `${formatBondCurrency(amount)} credit in review`;
}

/** The segment button's accessible name: the order, the kind, the amount, any
 *  credit in review, then what selecting it does. */
export function segmentAccessibleName(segment: AggregateSegment): string {
  const parts = [
    segment.orderNumber,
    segmentKindLabel(segment.kind).toLowerCase(),
    formatBondCurrency(segment.amount),
  ];
  if (lowBidder(segment)) parts.push("low bidder");
  if (segment.creditInReview !== null) {
    parts.push(creditPhrase(segment.creditInReview));
  }
  const action = segmentActionLabel(segment);
  return action ? `${parts.join(", ")}. ${action}` : parts.join(", ");
}

const NOTES: Record<SegmentKind, string> = {
  bid: "The full bid amount counts until the bid result is in.",
  pp: "The full contract counts.",
  other: "Counts toward the aggregate.",
};

/** Asked for only where one can be filed: a P&P in force (BRK-S404). */
const PAY_APPLICATION_ASK = "Upload a pay application to show progress.";

/** BRK-S442 — a bid the client was low on: PMS says so on the row (the
 *  served `bidResult`), and the bid stays held until the P&P issues. */
function lowBidder(segment: AggregateSegment): boolean {
  return segment.kind === "bid" && segment.bidResult === "low_bidder";
}

const LOW_BIDDER_NOTE =
  "Client was low · stays on at the bid amount until the performance and payment bond is issued.";

/** The row's note: what counts, and any credit in review. */
export function segmentNote(segment: AggregateSegment): string {
  const note = lowBidder(segment)
    ? LOW_BIDDER_NOTE
    : segment.kind === "pp" && segment.state === "locked"
      ? `${NOTES.pp} ${PAY_APPLICATION_ASK}`
      : NOTES[segment.kind];
  return segment.creditInReview === null
    ? note
    : `${note} · ${creditPhrase(segment.creditInReview)}`;
}

const money = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * BRK-S451 — where a P&P row's latest pay application stands, in words, from
 * the served `payApplication` (PMS-S849). Null when none is
 * filed. The SPA computes no release: "back" figures are PMS's own
 * (`estimatedRelease` while pending, `releasedAmount` once approved), shown
 * only when served, and a refusal carries no reason.
 */
export function payApplicationLine(
  pa: CapacityProjectPayApplication | null,
): string | null {
  if (pa === null) return null;
  const name = `Pay app #${String(pa.applicationNumber)}`;
  switch (pa.status) {
    case "pending": {
      const estimate = money(pa.estimatedRelease);
      return estimate === null
        ? `${name} in review`
        : `${name} in review · Estimated capacity back: ${formatBondCurrency(estimate)}`;
    }
    case "approved": {
      const released = money(pa.releasedAmount);
      return released === null
        ? `${name} approved`
        : `${name} approved · ${formatBondCurrency(released)} back in the program`;
    }
    case "denied":
      return `${name} not approved`;
    default:
      return `${name} · status unavailable`;
  }
}
